import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatApiError } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const tooShort = pwd.length > 0 && pwd.length < 8;
  const mismatch = confirm.length > 0 && confirm !== pwd;
  const canSubmit = token && pwd.length >= 8 && confirm === pwd && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: pwd,
      });
      toast.success("Mot de passe mis à jour. Connectez-vous avec votre nouveau mot de passe.");
      nav("/connexion?reset=1", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="cs-cream min-h-[80vh] py-16" data-testid="page-reset-password-missing-token">
        <div className="max-w-md mx-auto px-6">
          <div className="cs-eyebrow text-neutral-500">Espace client</div>
          <h1 className="cs-h1 mt-3 text-5xl">Lien incomplet.</h1>
          <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
            Ce lien de réinitialisation ne contient pas de jeton valide. Merci de refaire
            une demande depuis la page « Mot de passe oublié ».
          </p>
          <div className="mt-8">
            <Link
              to="/mot-de-passe-oublie"
              className="cs-btn-primary inline-flex justify-center"
              data-testid="reset-password-request-new"
            >
              Refaire une demande
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cs-cream min-h-[80vh] py-16" data-testid="page-reset-password">
      <div className="max-w-md mx-auto px-6">
        <div className="cs-eyebrow text-neutral-500">Espace client</div>
        <h1 className="cs-h1 mt-3 text-5xl">Nouveau mot de passe.</h1>
        <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
          Choisissez un nouveau mot de passe (8 caractères minimum). Vous serez ensuite
          redirigé·e vers la page de connexion.
        </p>

        <form className="mt-10 space-y-5" onSubmit={submit}>
          <div>
            <label htmlFor="reset-password" className="cs-eyebrow text-neutral-500">
              Nouveau mot de passe
            </label>
            <input
              id="reset-password"
              name="new_password"
              type="password"
              required
              minLength={8}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="cs-input mt-2"
              data-testid="reset-password-new-input"
              autoComplete="new-password"
            />
            {tooShort && (
              <p
                className="mt-2 text-xs text-red-600"
                data-testid="reset-password-short-hint"
              >
                8 caractères minimum.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="reset-confirm" className="cs-eyebrow text-neutral-500">
              Confirmer le mot de passe
            </label>
            <input
              id="reset-confirm"
              name="confirm_password"
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="cs-input mt-2"
              data-testid="reset-password-confirm-input"
              autoComplete="new-password"
            />
            {mismatch && (
              <p
                className="mt-2 text-xs text-red-600"
                data-testid="reset-password-mismatch-hint"
              >
                Les deux mots de passe ne correspondent pas.
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={!canSubmit}
            className="cs-btn-primary w-full justify-center"
            data-testid="reset-password-submit-button"
          >
            {loading ? "Mise à jour…" : "Enregistrer le nouveau mot de passe"}
          </button>
          <p className="text-sm text-neutral-600 pt-3">
            <Link
              to="/connexion"
              className="cs-link-underline text-[#0E7C90]"
              data-testid="reset-password-back-link"
            >
              ← Retour à la connexion
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
